import Link from "next/link";
import { cn } from "@/lib/utils";
import { BAND_TEXT_ON_DARK, ratingBand, ratingTextClass } from "@/features/judge/lib/rating-band";
import type { ContestCredit } from "../types";

/** The handle a credit prints: the username in its owner's capitals, the name when there is no username. */
export function creditHandle(person: ContestCredit): string {
  return person.usernameDisplay || person.username || person.name;
}

/**
 * `EX-CPG02`, Wave 30 decision 4: an author's handle in their rating band's
 * colour, the band's own shade for the ground it sits on (`docs/29` §7.6);
 * unrated is the ground's ordinary text colour. Linked to the profile when
 * there is a username, as every judge surface names a person.
 */
export function AuthorHandle({
  person,
  locale,
  tone,
  className,
}: {
  person: ContestCredit;
  locale: string;
  tone: "dark" | "light";
  className?: string;
}) {
  const rating = typeof person.rating === "number" ? person.rating : null;
  const colour =
    rating === null
      ? tone === "dark"
        ? "text-white"
        : "text-neutral-900"
      : tone === "dark"
        ? BAND_TEXT_ON_DARK[ratingBand(rating)]
        : ratingTextClass(rating);
  const classes = cn("font-bold", colour, className);
  return person.username ? (
    <Link
      href={`/${locale}/u/${person.username}`}
      className={cn(classes, "underline-offset-2 transition-colors duration-200 hover:underline")}
    >
      {creditHandle(person)}
    </Link>
  ) : (
    <span className={classes}>{creditHandle(person)}</span>
  );
}
